import React, { useEffect, useMemo, useState } from 'react';
import { Switch } from 'antd';
import { FabricObject } from 'fabric';
import { C } from '../../constants/design-tokens';
import { DEFAULT_ALPHA_FALLOFF_PARAMS, AlphaFalloffParams } from '../hooks/use-alpha-falloff';
import { SliderRow, buttonStyle, labelStyle, rowStyle } from './shared';

type AlphaFalloffControlsProps = {
  subject: FabricObject;
  modelParams: AlphaFalloffParams | undefined;
  setSubjectEffect: (
    subject: FabricObject,
    enabled: boolean,
    params: AlphaFalloffParams,
    commit?: boolean
  ) => Promise<AlphaFalloffParams>;
};

// Merge with defaults so a subject saved before a param existed still hydrates.
const readParams = (modelParams: AlphaFalloffParams | undefined): AlphaFalloffParams => ({
  ...DEFAULT_ALPHA_FALLOFF_PARAMS,
  ...modelParams
});

export const AlphaFalloffControls: React.FC<AlphaFalloffControlsProps> = ({
  subject,
  modelParams,
  setSubjectEffect
}) => {
  const [enabled, setEnabled] = useState(!!modelParams);
  const [params, setParams] = useState<AlphaFalloffParams>(() => readParams(modelParams));

  useEffect(() => {
    setEnabled(!!modelParams);
    setParams(readParams(modelParams));
  }, [subject, modelParams]);

  // Live preview
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const effective = await setSubjectEffect(subject, enabled, params, false);
      if (cancelled) return;
      if (JSON.stringify(effective) !== JSON.stringify(params)) setParams(effective);
    }, 30);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [subject, enabled, params, setSubjectEffect]);

  const commit = async (nextEnabled: boolean, nextParams: AlphaFalloffParams) => {
    const effective = await setSubjectEffect(subject, nextEnabled, nextParams, true);
    if (JSON.stringify(effective) !== JSON.stringify(nextParams)) setParams(effective);
  };

  const onToggle = (next: boolean) => {
    setEnabled(next);
    void commit(next, params);
  };

  const commitParams = () => void commit(enabled, params);

  const update = useMemo(() => (patch: Partial<AlphaFalloffParams>) => setParams(p => ({ ...p, ...patch })), []);

  // Reset the style knobs but keep the auto-fitted geometry so the effect
  // stays anchored to the subject.
  const reset = () => {
    const next: AlphaFalloffParams = {
      ...DEFAULT_ALPHA_FALLOFF_PARAMS,
      bandX: params.bandX,
      bandXSmooth: params.bandXSmooth,
      ceiling: params.ceiling,
      floor: params.floor
    };
    setParams(next);
    void commit(enabled, next);
  };

  return (
    <div style={{ padding: '6px 14px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={rowStyle}>
        <span style={{ ...labelStyle, color: C.textPrimary }}>Habilitar</span>
        <Switch size='small' checked={enabled} onChange={onToggle} />
      </div>

      {enabled && (
        <>
          <SliderRow
            label='alto'
            value={params.bandHeightRatio}
            min={0}
            max={1}
            step={0.01}
            onChange={bandHeightRatio => update({ bandHeightRatio })}
            onCommit={commitParams}
          />
          <SliderRow
            label='waist smooth'
            value={params.waistSmooth}
            min={0}
            max={0.3}
            step={0.001}
            onChange={waistSmooth => update({ waistSmooth })}
            onCommit={commitParams}
          />
          <SliderRow
            label='curvature'
            value={params.waistCurvature}
            min={-1.5}
            max={1.5}
            step={0.01}
            onChange={waistCurvature => update({ waistCurvature })}
            onCommit={commitParams}
          />

          <button onClick={reset} style={{ ...buttonStyle, alignSelf: 'flex-start' }}>
            Reset valores
          </button>
        </>
      )}
    </div>
  );
};
